/* eslint-disable jsx-a11y/no-static-element-interactions */
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { CircularProgress, Fab, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { AutoCompleteWithLabel } from 'commons/AutoComplete/AutoCompleteWithLabel';
import { AutoCompleteWithValue } from 'commons/AutoComplete/AutoCompleteWithValue';
import { DialogType } from 'commons/Dialog/DialogTypes';
import CustomIcon from 'commons/Icon/CustomIcon';
import LocalizedText, { localize } from 'commons/LocalizedText/LocalizedText';
import { SimpleText } from 'commons/SimpleText/SimpleText';
import { TextFieldWithLabel } from 'commons/TextFieldWithLabel/TextFieldWithLabel';
import { useDialog } from 'hook/DialogProvider';
import useFetch from 'hook/UseFetch';
import useLocalizedSnackBar from 'hook/UseLocalizedSnackBar';
import { useUserInfo } from 'hook/UseUserInfo';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CompanyService } from 'services/CompanyService';
import { LaboratoryService } from 'services/LaboratoryService';
import { LanguageService } from 'services/LanguageService';
import { ProgramService } from 'services/ProgramService';
import { UserService } from 'services/UserService';
import { ErrorName } from 'utils/constants/error.name';
import { isEcc, isLaboAdmin, isSuperAdmin } from 'utils/constants/roles';
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react-hooks/exhaustive-deps */
import { GENERATE_ROUTE } from 'utils/constants/routes';

const useStyles = makeStyles((theme) => ({
  controlsContainer: {
    position: 'fixed',
    left: '90%',
    bottom: '15%',
    textAlign: 'center',
    width: '5%'
  },
  saveContainer: {
    cursor: 'pointer',
    position: 'absolute',
    height: 90,
    width: 90,
    fontSize: '200%'
  },
  saveIcon: {
    color: theme.palette.background.paper
  },
  title: {
    color: theme.palette.secondary.main,
    marginBottom: '30px',
    '&::before': {
      content: '"-"',
      backgroundColor: theme.palette.secondary.main,
      marginRight: '10px'
    }
  },
  relatedProgramsRequirement: {
    color: theme.palette.error.main,
    fontSize: '14px',
    marginLeft: '20px'
  },
  relatedProgramsAll: {
    marginRight: '516px'
  },
  autocompleteWithValue: {
    maxWidth: 400,
    minWidth: 400
  },
  dropdown: {
    width: 300,
    margin: 0
  },
  columnContainer: {
    width: '700px'
  }
}));

const FIELDS = {
  LAST_NAME: {
    name: 'lastName',
    label: 'contact.lastName',
    required: true
  },
  FIRST_NAME: {
    name: 'firstName',
    label: 'contact.firstName',
    required: true
  },
  CIVILITY: {
    name: 'civility',
    label: 'contact.civility',
    required: true
  },
  PHONE_NUMBER: {
    name: 'phoneNumber',
    label: 'contact.phoneNumber',
    required: false
  },
  CONTACT_EMAIL: {
    name: 'contactEmail',
    label: 'contact.mail',
    required: true
  },
  LABORATORY: {
    label: 'contact.laboratory'
  },
  COMPANY: {
    name: 'company',
    label: 'contact.company',
    required: true
  },
  ACCOUNT_EMAIL: {
    name: 'loginEmail',
    label: 'account.generalEmail',
    required: true
  },
  PROFILE: {
    name: 'profile',
    label: 'account.profile',
    required: true
  },
  STATUS: {
    name: 'status',
    label: 'account.status',
    required: true
  },
  LANGUAGE: {
    name: 'language',
    label: 'account.language',
    required: true
  },
  RELATED_PROGRAMS: {
    all: 'all',
    label: 'details.programs',
    name: 'programs',
    required: true
  }
}

export const LaboUserEdit = ({laboratoryId, personContactDetailsId, isCreation, redirect}) => {
  const classes = useStyles();
  const showDialog = useDialog();
  const history = useHistory();
  const {role} = useUserInfo();
  const showSnackbar = useLocalizedSnackBar();
  const [laboUserInfo, setLaboUserInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const { response: availableCivilities, isLoading: loadingCivilities } = useFetch(() => UserService.getUserCivilitiesNames(), []);
  const { response: availableStatuses, isLoading: loadingStatuses } = useFetch(() => UserService.getUserStatusesNames(), []);
  const { response: availableLanguages, isLoading: loadingLanguages } = useFetch(() => LanguageService.getLocales(), []);
  const { response: availableRoles, isLoading: loadingRoles } = useFetch(() => UserService.getUserRolesNames(), []);
  const { response: availableLaboratories, isLoading: loadingLaboratories } = useFetch(() => LaboratoryService.getLaboratories(), []);
  const { response: availableCompanies, isLoading: loadingCompanies } = useFetch(() => CompanyService.getCompanies({isEcc: true}), []);
  const [errors, setErrors] = useState({
    [FIELDS.PHONE_NUMBER]: false,
    [FIELDS.ACCOUNT_EMAIL]: false
  });

  const getValue = (field) => {
    return laboUserInfo?.[field.name]
  }

  useEffect(() => {
    if (!isCreation && personContactDetailsId) {
      UserService.getLaboUserDetailedInfo(personContactDetailsId).then((resp) => {
        setLaboUserInfo(resp);
      })
        .catch((errorMessage) => {
          if (errorMessage !== ErrorName.ABORT_ERROR) {
            showDialog({
              variant: DialogType.ERROR,
              title: localize('error'),
              description: errorMessage
            });
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [personContactDetailsId, laboratoryId])

  useEffect(() => {
    if (isCreation) {
      setIsLoading(loadingLaboratories || loadingCompanies)
    }
  }, [loadingLaboratories, loadingCompanies])

  useEffect(() => {
  }, [isLoadingPrograms])

  useEffect(() => {
    if (!loadingLaboratories && laboUserInfo?.[FIELDS.COMPANY.name]) {
      setIsLoadingPrograms(true);
      ProgramService.getProgramsByLaboratoryId(laboUserInfo?.[FIELDS.COMPANY.name].id).then(resp => {
        setAvailablePrograms(resp);
      })
        .finally(() => {
          setIsLoadingPrograms(false);
        });
    }
  }, [loadingLaboratories, laboUserInfo?.[FIELDS.COMPANY.name]])

  useEffect(() => {
    if (isCreation && onlyOneLaboOrCompany()) {
      onInfoChange(shouldDisplayLaboratoryInsteadOfCompany() ? availableLaboratories[0] : availableCompanies[0]  , FIELDS.COMPANY)
    }
  }, [isLoading])

  const shouldDisplayLaboratoryInsteadOfCompany = () => {
    return getValue(FIELDS.COMPANY)?.isLaboratory
      || !isEcc(laboUserInfo?.[FIELDS.PROFILE.name]);
  }

  const onlyOneLaboOrCompany = () => {
    return (shouldDisplayLaboratoryInsteadOfCompany() ? availableLaboratories.length : availableCompanies.length) === 1;
  }

  const selectLaboratoryOrCompanyField = () => {
    return shouldDisplayLaboratoryInsteadOfCompany() ? FIELDS.LABORATORY : FIELDS.COMPANY
  }

  useEffect(() => {
    if (isCreation) {
      onInfoChange(null, FIELDS.LABORATORY);
      onInfoChange(null, FIELDS.COMPANY);
    }
  }, [shouldDisplayLaboratoryInsteadOfCompany()])

  const onInfoChange = (value, field) => {
    if (!isLoading) {
      setLaboUserInfo((oldInfo) => {
        const userInfo = {...oldInfo};
        if (!value) {
          delete userInfo[field.name];
        } else {
          userInfo[field.name] = value;
          if (field.name === FIELDS.PROFILE.name && isEcc(value)) {
            delete userInfo[FIELDS.RELATED_PROGRAMS.name];
          }
        }
        return userInfo;
      });
    }
  }

  const handleSave = () => {
    if (!validateRequiredFields()) {
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: "missingRequiredParameters"
      });
    } else if (!validateFormatFields()) {
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: "invalidParametersFormat"
      });
    } else {
      setIsLoadingSave(true);
      if (isCreation) {
        UserService.createLaboUser(laboUserInfo).then((resp) => {
          setLaboUserInfo(resp);
          showSnackbar('notification.user.created', { variant: 'success' });
          history.replace(GENERATE_ROUTE(redirect, {laboratory: laboratoryId, personContactDetailsId: resp?.personContactDetailsRecid}));
        }).catch((error) => {
          showDialog({
            variant: DialogType.ERROR,
            title: 'error',
            description: error
          });
        }).finally(() => setIsLoadingSave(false));
      } else {
        UserService.updateLaboUserDetailedInfo(personContactDetailsId, laboUserInfo).then((resp) => {
          setLaboUserInfo(resp);
          showSnackbar('notification.user.edited', { variant: 'success' });
        }).catch((error) => {
          showDialog({
            variant: DialogType.ERROR,
            title: 'error',
            description: error
          });
        }).finally(() => setIsLoadingSave(false));
      }
    }
  }

  const validateRequiredFields = () => {
    const requiredFields = Object.values(FIELDS).filter(field => field.required);
    return requiredFields.every(field => {
      const value = laboUserInfo[field.name];
      if (field.name === FIELDS.RELATED_PROGRAMS.name && isEcc(laboUserInfo[FIELDS.PROFILE.name])) return true;
      if(!value) return false;
      if(Array.isArray(value)) return value.length > 0;
      return value !== '';
    })
  }

  const validateFormatFields = () => {
    return Object.values(errors).every(v => !!v === false)
  }

  const setError = (field, value) => {
    setErrors(oldErrors => {
      const newErrors = { ...oldErrors };
      newErrors[field.name] = value;
      return newErrors;
    });
  }

  return (
    <>
      <Grid container direction='column' spacing={5}>
        <Grid item>
          <Grid container direction='row' justifyContent="space-between" spacing={10}>
            <Grid item xs={5}>
              <Grid
                className={classes.columnContainer}
                container
                direction='column'
                spacing={1}
              >
                <Grid item>
                  <LocalizedText className={classes.title}>section.contactInformation</LocalizedText>
                </Grid>
                <Grid item>
                  {!isLoading ? (
                    <Grid
                      className={classes.columnContainer}
                      container
                      direction='column'
                      spacing={1}
                    >
                      <AutoCompleteWithLabel
                        className={`${classes.dropdown}`}
                        enableColorChangeOnModification
                        getOptionLabel={civ => localize(String(civ?.id)) || ''}
                        isLoading={isLoading || loadingCivilities}
                        label={FIELDS.CIVILITY.label}
                        onChange={value => onInfoChange(value, FIELDS.CIVILITY)}
                        options={availableCivilities}
                        required={FIELDS.CIVILITY.required}
                        value={laboUserInfo?.[FIELDS.CIVILITY.name] || null}
                        variant='outlined'
                      />
                      <TextFieldWithLabel
                        enableColorChangeOnModification
                        label={FIELDS.LAST_NAME.label}
                        onChange={value => onInfoChange(value, FIELDS.LAST_NAME)}
                        required={FIELDS.LAST_NAME.required}
                        value={laboUserInfo?.[FIELDS.LAST_NAME.name] || ''}
                      />
                      <TextFieldWithLabel
                        enableColorChangeOnModification
                        label={FIELDS.FIRST_NAME.label}
                        onChange={value => onInfoChange(value, FIELDS.FIRST_NAME)}
                        required={FIELDS.FIRST_NAME.required}
                        value={laboUserInfo?.[FIELDS.FIRST_NAME.name] || ''}
                      />
                      <TextFieldWithLabel
                        enableColorChangeOnModification
                        label={FIELDS.PHONE_NUMBER.label}
                        onChange={value => onInfoChange(value, FIELDS.PHONE_NUMBER)}
                        setError={error => setError(FIELDS.PHONE_NUMBER, error)}
                        type='phone'
                        value={laboUserInfo?.[FIELDS.PHONE_NUMBER.name] || ''}
                      />
                      <TextFieldWithLabel
                        enableColorChangeOnModification
                        label={FIELDS.CONTACT_EMAIL.label}
                        onChange={value => onInfoChange(value, FIELDS.CONTACT_EMAIL)}
                        required={FIELDS.CONTACT_EMAIL.required}
                        setError={error => setError(FIELDS.CONTACT_EMAIL, error)}
                        type="email"
                        value={laboUserInfo?.[FIELDS.CONTACT_EMAIL.name] || ''}
                      />
                      {( isCreation ?
                        (
                          <AutoCompleteWithLabel
                            className={classes.dropdown}
                            enableColorChangeOnModification
                            getOptionLabel={lab => lab?.name || ''}
                            isLoading={isLoading || shouldDisplayLaboratoryInsteadOfCompany() ? loadingLaboratories : loadingCompanies}
                            label={selectLaboratoryOrCompanyField().label}
                            onChange={value => onInfoChange(value, FIELDS.COMPANY)}
                            options={shouldDisplayLaboratoryInsteadOfCompany() ? availableLaboratories : availableCompanies}
                            required={FIELDS.COMPANY.required}
                            value={laboUserInfo?.[FIELDS.COMPANY.name] || null}
                            variant='outlined'
                          />
                        ) : (
                          <SimpleText
                            label={selectLaboratoryOrCompanyField().label}
                            value={laboUserInfo?.[FIELDS.COMPANY.name]?.name || ''}
                          />
                        )
                      )}
                    </Grid>
                  ) : (<Skeleton height={400} variant="rect" width={600}/>)}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid alignContent="center" container direction='column' spacing={1}>
                <Grid item>
                  <LocalizedText className={classes.title}>section.account</LocalizedText>
                </Grid>
                <Grid item>
                  {!isLoading ? (
                    <Grid className={classes.columnContainer} container direction='column' spacing={1}>
                      {isCreation ?
                        <TextFieldWithLabel
                          enableColorChangeOnModification
                          label={localize(FIELDS.ACCOUNT_EMAIL.label)}
                          onChange={value => onInfoChange(value, FIELDS.ACCOUNT_EMAIL)}
                          required={FIELDS.ACCOUNT_EMAIL.required}
                          setError={error => setError(FIELDS.ACCOUNT_EMAIL, error)}
                          type="email"
                          value={laboUserInfo?.[FIELDS.ACCOUNT_EMAIL.name] || ''}
                        /> : <SimpleText
                          label={localize(FIELDS.ACCOUNT_EMAIL.label)}
                          value={laboUserInfo?.[FIELDS.ACCOUNT_EMAIL.name] || ''}
                        />
                      }
                      {isLaboAdmin(role) && (availableRoles?.some(role => role === getValue(FIELDS.PROFILE)) || !getValue(FIELDS.PROFILE)) ?
                        <AutoCompleteWithLabel
                          className={classes.dropdown}
                          enableColorChangeOnModification
                          getOptionLabel={option => localize(option) || ''}
                          isLoading={isLoading || loadingRoles}
                          isTranslatingOptions
                          label={FIELDS.PROFILE.label}
                          onChange={value => onInfoChange(value, FIELDS.PROFILE)}
                          options={availableRoles.filter(profile => isSuperAdmin(role) || !isEcc(profile))}
                          required={FIELDS.PROFILE.required}
                          value={laboUserInfo?.[FIELDS.PROFILE.name] || null}
                          variant='outlined'
                        />
                        :
                        <SimpleText
                          label={FIELDS.PROFILE.label}
                          value={localize(laboUserInfo?.profile)}
                        />
                      }
                      {isLaboAdmin(role) ?
                        <AutoCompleteWithLabel
                          className={classes.dropdown}
                          enableColorChangeOnModification
                          isLoading={isLoading || loadingStatuses}
                          isTranslatingOptions
                          label={FIELDS.STATUS.label}
                          onChange={value => onInfoChange(value, FIELDS.STATUS)}
                          options={availableStatuses}
                          required={FIELDS.STATUS.required}
                          value={laboUserInfo?.[FIELDS.STATUS.name] || null}
                          variant='outlined'
                        />:
                        <SimpleText
                          label={FIELDS.STATUS.label}
                          value={localize(laboUserInfo?.[FIELDS.STATUS.name])}
                        />
                      }
                      <AutoCompleteWithLabel
                        className={classes.dropdown}
                        enableColorChangeOnModification
                        getOptionLabel={(language) => localize(language?.name) || ''}
                        getOptionSelected={(option, value) => value.text === option.text}
                        isLoading={isLoading || loadingLanguages}
                        label={FIELDS.LANGUAGE.label}
                        onChange={value => onInfoChange(value, FIELDS.LANGUAGE)}
                        options={availableLanguages}
                        required={FIELDS.LANGUAGE.required}
                        value={laboUserInfo?.[FIELDS.LANGUAGE.name] || null}
                        variant='outlined'
                      />
                    </Grid>
                  ) : (<Skeleton height={400} variant="rect" width={600}/>)}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction='column' item spacing={1}>
            <Grid item>
              <LocalizedText className={classes.title}>section.relatedPrograms</LocalizedText>
            </Grid>
            {!isLoading ?
              isEcc(getValue(FIELDS.PROFILE)) ?
                <Grid className={classes.relatedProgramsAll}>
                  <SimpleText
                    label={localize(FIELDS.RELATED_PROGRAMS.label)}
                    value={localize(FIELDS.RELATED_PROGRAMS.all)}
                  />
                </Grid> :
                <>
                  {!laboUserInfo?.[FIELDS.COMPANY.name] && (<Grid item>
                    <LocalizedText className={classes.relatedProgramsRequirement}>relatedProgramsRequirement</LocalizedText>
                  </Grid>)}
                  <Grid item>
                    <AutoCompleteWithValue
                      className={classes.autocompleteWithValue}
                      disabled={!isLaboAdmin(role) || !laboUserInfo?.[FIELDS.COMPANY.name] || isLoadingPrograms}
                      getOptionLabel={(program) => String(program?.text) || ''}
                      isLoading={isLoading || isLoadingPrograms}
                      isMultiple
                      label={FIELDS.RELATED_PROGRAMS.label}
                      onChange={(value) => onInfoChange(value, FIELDS.RELATED_PROGRAMS)}
                      options={availablePrograms}
                      required={FIELDS.RELATED_PROGRAMS.required}
                      value={laboUserInfo?.[FIELDS.RELATED_PROGRAMS.name] || []}
                      variant='outlined'
                    />
                  </Grid>
                </>: (<Skeleton height={200} variant="rect" width={600}/>)}
          </Grid>
        </Grid>
      </Grid>
      <Grid className={classes.controlsContainer} container direction='column'>
        <Grid item>
          <Fab
            aria-label="add"
            className={classes.saveContainer}
            color="primary"
            disabled={isLoadingSave || !validateRequiredFields()}
            onClick={handleSave}
          >
            {isLoadingSave ?
              <CircularProgress color='white'/>
              :
              <CustomIcon className={classes.saveIcon} icon={faSave}/>
            }
          </Fab>
        </Grid>
      </Grid>
    </>
  )
}